import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LockedSpeechCTA from './LockedSpeechCTA';
import { fetchSpeechThumbnail } from '../services/api';

export default function SpeechCard({
  speech, showSpeaker = true, feedbackCount, onDelete,
  // Locked-card wiring. SpeechCard stays presentational: the parent owns the
  // request call and feeds the resulting state back in.
  onRequestFriend, friendRequestState = 'none',
}) {
  const {
    speech_id,
    speaker_id,
    title,
    speaker_name,
    youtube_url,
    synopsis,
    metrics,
    locked = false,
    has_video = false,
    speaker_provisional = false,
    tags = [],
    created_at,
    meeting_date,
  } = speech;

  // Proxied posters (locked cards, and member uploads which have no YouTube
  // thumbnail) need the bearer token an <img src> cannot send — so fetch the
  // bytes and use an object URL instead.
  const [proxyPoster, setProxyPoster] = useState(null);
  const needsProxyPoster = has_video && (locked || !youtube_url);
  useEffect(() => {
    if (!needsProxyPoster) return undefined;
    let objectUrl = null;
    let cancelled = false;
    fetchSpeechThumbnail(speech_id)
      .then((u) => {
        if (cancelled) { if (u) URL.revokeObjectURL(u); return; }
        objectUrl = u;
        setProxyPoster(u);
      })
      .catch(() => { /* no poster — the lock panel renders without one */ });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [needsProxyPoster, speech_id]);

  const getThumbnail = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|[?&]v=|\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
  };

  const thumbnail = getThumbnail(youtube_url) || (!locked ? proxyPoster : null);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const d = new Date(Number(timestamp));
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const initials = speaker_name
    ? speaker_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  const confidence = metrics?.confidence_score;

  const ringColor = confidence == null ? 'var(--border-subtle)'
    : confidence >= 70 ? 'var(--color-success)'
    : confidence >= 40 ? 'var(--accent)'
    : 'var(--color-danger)';

  const RING_R = 16;
  const RING_CIRC = 2 * Math.PI * RING_R;
  const ringDash = confidence != null ? (confidence / 100) * RING_CIRC : 0;


  const visibleTags = (tags || []).slice(0, 4);

  return (
    <Link
      to={`/speeches/${speech_id}`}
      className="block overflow-hidden group rounded-2xl relative"
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.borderColor = 'var(--border-accent)';
        e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.12), 0 0 20px var(--accent-glow)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.borderColor = 'var(--border-subtle)';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* 16:9 thumbnail */}
      <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
        {locked ? (
          <>
            {/* Poster frame comes from our own proxy, never from img.youtube.com —
                that URL embeds the video id, which is the video. See the route. */}
            {proxyPoster ? (
              <img
                src={proxyPoster}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover"
                style={{ filter: 'saturate(0.55) brightness(0.55)' }}
              />
            ) : (
              <div className="w-full h-full" style={{ background: 'var(--bg-input)' }} />
            )}

            {/* Lock chip, always visible so the state is obvious at a glance. */}
            <div
              className="absolute flex items-center gap-1 px-2 py-0.5 rounded-pill"
              style={{
                top: 8, left: 8, zIndex: 2,
                background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 10 }}>🔒</span>
              <span
                className="font-mono text-[9px] uppercase tracking-[0.18em]"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                {speaker_provisional ? 'Not yet joined' : 'Friends only'}
              </span>
            </div>

            {/* The ask, centred on the poster. Always rendered rather than
                hover-only: there is no hover on touch, and a hidden primary
                action is unreachable there. Hover just raises the contrast. */}
            <div
              className="absolute inset-0 flex items-center justify-center transition-all duration-300"
              style={{ zIndex: 3, background: 'rgba(0,0,0,0.28)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.28)'; }}
            >
              <LockedSpeechCTA
                speakerId={speaker_id}
                speakerName={speaker_name}
                isProvisional={speaker_provisional}
                state={friendRequestState}
                onRequest={onRequestFriend}
                stopClick
                prominent
              />
            </div>
          </>
        ) : thumbnail ? (
          <img
            src={thumbnail}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, var(--accent-glow) 0%, var(--color-info-dim) 100%)' }}
          >
            <span className="text-4xl opacity-50">🎤</span>
          </div>
        )}

        {/* Score ring — bottom-right of thumbnail, glassy backdrop */}
        {metrics && confidence != null && (
          <div
            className="absolute"
            style={{
              bottom: 8, right: 8, width: 40, height: 40, zIndex: 2,
              borderRadius: '50%',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              background: 'rgba(0,0,0,0.45)',
              boxShadow: '0 1px 6px rgba(0,0,0,0.35)',
            }}
          >
            <svg
              width="40" height="40" viewBox="0 0 40 40"
              role="img"
              aria-label={`AI score: ${Math.round(confidence)} out of 100`}
            >
              {/* Track */}
              <circle
                cx="20" cy="20" r={RING_R}
                fill="none"
                stroke="rgba(255,255,255,0.18)"
                strokeWidth="2.5"
              />
              {/* Score arc — starts at 12 o'clock */}
              <circle
                cx="20" cy="20" r={RING_R}
                fill="none"
                stroke={ringColor}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={`${ringDash} ${RING_CIRC}`}
                transform="rotate(-90 20 20)"
              />
              <text
                x="20" y="18"
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="9"
                fontWeight="700"
                fontFamily="'Outfit', system-ui, sans-serif"
                fill="rgba(255,255,255,0.95)"
              >
                {Math.round(confidence)}
              </text>
              <text
                x="20" y="28"
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="6"
                fontWeight="600"
                fontFamily="'Outfit', system-ui, sans-serif"
                fill="rgba(255,255,255,0.6)"
              >
                score
              </text>
            </svg>
          </div>
        )}

        {/* Top badges — always on dark overlay so keep dark styles */}
        <div className="absolute top-2 left-2 right-2 flex items-start justify-between">
          {feedbackCount !== undefined && (
            <span
              className="px-2 py-0.5 text-xs rounded-pill font-bold"
              style={{
                background: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.9)',
              }}
            >
              🙌 {feedbackCount} {feedbackCount === 1 ? 'ovation' : 'ovations'}
            </span>
          )}
          {/* The lock now lives on the thumbnail itself (see above), so no badge
              here — is_public is retired and no longer means anything. */}
          {onDelete && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(); }}
              aria-label="Delete video"
              className="ml-auto flex items-center justify-center rounded-full text-xs font-bold"
              style={{
                width: 24, height: 24,
                background: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.9)',
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Card body */}
      <div className="px-3 pt-2.5 pb-3 space-y-1.5">
        {/* Title */}
        <h3
          className="font-bold line-clamp-2 leading-snug"
          style={{ color: 'var(--text-primary)', fontSize: '1.025rem' }}
        >
          {title}
        </h3>

        {/* Speaker row */}
        {showSpeaker && speaker_name && (
          <div className="flex items-center gap-1.5">
            <div
              className="flex-shrink-0 flex items-center justify-center rounded-full text-xs font-bold"
              style={{
                width: 22, height: 22,
                background: 'var(--border-accent)',
                color: 'var(--accent)',
              }}
            >
              {initials}
            </div>
            <p className="text-xs font-medium truncate" style={{ color: 'var(--text-secondary)' }}>{speaker_name}</p>
          </div>
        )}

        {/* Synopsis */}
        {synopsis && (
          <p className="text-xs line-clamp-2 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
            {synopsis}
          </p>
        )}

        {/* Tags */}
        {visibleTags.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-0.5">
            {visibleTags.map(tag => (
              <span
                key={tag}
                className="px-2 py-0.5 rounded-pill text-xs font-semibold"
                style={{ background: 'var(--color-info-dim)', color: 'var(--color-info)', border: '1px solid var(--color-info-border)' }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Date */}
        <div className="flex items-center pt-0.5">
          <p className="text-xs font-bold uppercase tracking-wider ml-auto" style={{ color: 'var(--text-faint)' }}>
            {formatDate(created_at || meeting_date)}
          </p>
        </div>
      </div>
    </Link>
  );
}
